import { CombinedCustomerAuditCategory } from "Hooks/Api/apiCustomerAudits";
import { atbStatus, customerApplicationNoteType, DataTypeSource, referrerCommissionFrequency } from "Models/Enums";
import { LocaleCompareObjectAsc } from "Util/StringUtils";
import { ComboboxOption } from "Views/Components/Combobox/Combobox";
import { accountType, aggregatedEsignWorkflowStatus, atbFileType, auditCategory, automatedPpsrGoniSource, customerApplicationStatus, CustomerApplicationTradeReferenceStatus, customerAuditCategory, CustomerCreationSource, esignworkflowstatus, organisationCategory, processingStatus, referrerCommissionFrequencyOptions, SerialNumberType, userType, WatercraftSerialNumberType } from "../Enums";


export const atbStatusOptions: { [key in atbStatus]: string } = {
	CREATED: 'Created',
	VALIDATING: 'Validating',
	VALIDATED: 'Validated',
	QUEUED_FOR_SUBMISSION: 'Queued For Submission',
	SUCCESS: 'Success',
	FAILED: 'Failed',
	CANCELLED: 'Cancelled',
}

/**
 * Mapping of the AtbFile states to a human readable information
 */
export const atbStatusDetailOptions: { [key in atbStatus]: string } = {
	CREATED: 'Created',
	VALIDATING: 'Processing',
	VALIDATED: 'Ready for submission',
	QUEUED_FOR_SUBMISSION: 'Queued for submission',
	SUCCESS: 'Completed',
	FAILED: 'Failed',
	CANCELLED: 'Cancelled',
}


export const customerApplicationStatusComboboxOptions: ComboboxOption<customerApplicationStatus | 'ALL'>[] = [
	{ display: 'All Statuses', value: 'ALL' },
	{ display: 'Pending', value: 'PENDING' },
	{ display: 'Approved', value: 'APPROVED' },
	{ display: 'Referred', value: 'REFERRED' },
	{ display: 'Declined', value: 'DECLINED' },
	{ display: 'Not Started', value: 'NOT_STARTED' },
	{ display: 'Incomplete', value: 'INCOMPLETE' },
	{ display: 'Withdrawn', value: 'WITHDRAWN' },
];

export const processingStatusDisplayOptions: { [key in processingStatus]: string } = {
	CREATED: 'Requested',
	PROCESSING: 'Generating',
	PROCESSED: 'Completed',
	ERROR: 'Error',
};

export const customerApplicationTradeReferenceStatusComboboxOptions: ComboboxOption<CustomerApplicationTradeReferenceStatus>[] = [
	{ display: "Pending", value: "PENDING" },
	{ display: "Submitted", value: "SUBMITTED" },
	{ display: "Declined", value: "DECLINED" }
]

export const customerApplicationTradeReferenceStatusOptions: { [key in CustomerApplicationTradeReferenceStatus]: string } = {
	PENDING: 'Pending',
	SUBMITTED: 'Submitted',
	DECLINED: 'Declined',
};

export const customerApplicationStatusOptions: { [key in customerApplicationStatus]: string } = {
	PENDING: 'Pending',
	APPROVED: 'Approved',
	REFERRED: 'Referred',
	DECLINED: 'Declined',
	NOT_STARTED: 'Not Started',
	INCOMPLETE: 'Incomplete',
	WITHDRAWN: 'Withdrawn',
	UNKNOWN: 'Unknown',
};

export const esignWorkflowStatusOptions: { [key in esignworkflowstatus]: string } = {
	CREATED: 'Created',
	PROCESSING: 'Processing',
	COMPLETED: 'Completed',
	INCOMPLETE: 'Incomplete',
	CANCELED: 'Cancelled',
	REJECTED: 'Rejected',
};

export const aggregatedEsignWorkflowStatusOptions: { [key in aggregatedEsignWorkflowStatus]: string } = {
	CREATED: 'Created',
	PROCESSING: 'Processing',
	COMPLETED: 'Completed',
	INCOMPLETE: 'Incomplete',
	CANCELED: 'Cancelled',
	REJECTED: 'Rejected',
	MIXED: 'Mixed',
	NONE: 'None',
};

export const accountTypeOptions: { [key in accountType]: string } = {
	INVOICED: 'Invoice',
	PAYG: 'Credit Card',
};

export const organisationAccountTypeComboboxOptions: ComboboxOption<accountType>[] = (
	(Object.keys(accountTypeOptions) as [accountType])
		.map<ComboboxOption<accountType>>(value => {
			return {
				display: accountTypeOptions[value],
				value: value,
			};
		})
);

export const organisationCategoryComboboxOptions: ComboboxOption<organisationCategory>[] = [
	{ display: "Extra Small", value: "PAYG"},
	{ display: "Small", value: "SMALL"},
	{ display: "Medium", value: "MEDIUM"},
	{ display: "Large", value: "LARGE"},
	{ display: "Enterprise", value: "ENTERPRISE"},
];

export const customerAuditCategoryComboboxOptions: ComboboxOption<CombinedCustomerAuditCategory | 'ALL'>[] = [
	{ display: 'All Update Types', value: 'ALL' },
	{ display: 'Account Information', value: 'ACCOUNT_INFORMATION' },
	{ display: 'Adverse Alert', value: 'ADVERSE_ALERT' },
	{ display: 'Business Information', value: 'BUSINESS_INFORMATION' },
	{ display: 'Comment', value: 'COMMENT' },
	{ display: 'Credit Default', value: 'CREDIT_DEFAULT' },
	{ display: 'Entity Update', value: 'ENTITY_UPDATE' },
	{ display: 'Risk Score', value: 'RISK_SCORE' },
	{ display: 'Other', value: 'OTHER' },
];

export const atbFileTypeComboboxOptions: ComboboxOption<atbFileType | 'ALL'>[] = [
	{ display: 'All Types', value: 'ALL' },
	{ display: 'Xero', value: 'XERO'},
	{ display: 'Standard', value: 'STANDARD'},
	{ display: 'Unknown', value: 'UNKNOWN'},
	{ display: 'Customers Only', value: 'CUSTOMERS_ONLY'},
];

export const referrerCommissionFrequencyComboboxOptions = () : ComboboxOption<string>[] => {
	const values = Object.keys(referrerCommissionFrequencyOptions);

	return values
		.sort(LocaleCompareObjectAsc(referrerCommissionFrequencyOptions))
		.map<ComboboxOption<string>>((value, index, array) => {
			return { display: referrerCommissionFrequencyOptions[value as referrerCommissionFrequency], value: value };
		});
};

// Options in alphabetic order
export const referrerBusinessTypeRegistrationComboboxOptions: ComboboxOption<string>[] = [
	{ display: 'Credit bureau', value: 'CREDIT_BUREAU' },
	{ display: 'Credit insurance broker', value: 'CREDIT_INSURANCE_BROKER' },
	{ display: 'Debt collection', value: 'DEBT_COLLECTION' },
	{ display: 'I am an individual', value: 'INDIVIDUAL' },
	{ display: 'Insolvency practitioner', value: 'INSOLVENCY_PRACTITIONER' },
	{ display: 'Lawyer', value: 'LAWYER' },
	{ display: 'Other (provide details)', value: 'OTHER' },
];

export const referrerBenefitTypeRegistrationComboboxOptions: ComboboxOption<string>[] = [
	{ display: 'Client to receive overall discount', value: 'CLIENT_DISCOUNT' },
	{ display: 'Do not receive commission', value: 'NO_COMMISSION' },
	{ display: 'Receive commission', value: 'COMMISSION' },
	{ display: 'Send commission to charity', value: 'CHARITY' },
];

export const dataTypeSourceOptions: { [key in DataTypeSource]: string } = {
	Live: 'Live Data',
	Internal: 'Last Synced',
	Unavailable: 'Unavailable',
};

export const dataTypeSourceIcons: { [key in DataTypeSource]: {
	icon: string,
} } = {
	Live: {
		icon: 'globe',
	},
	Internal: {
		icon: 'wifi-off',
	},
	Unavailable: {
		icon: 'warning-2',
	},
};

export const customerCreationSourceOptions: { [key in CustomerCreationSource]: string } = {
	UNKNOWN: 'Unknown',
	MONITOR_CUSTOMER: 'Monitor Customer',
	ATB_UPLOAD: 'ATB Upload',
	CUSTOMER_UPLOAD: 'Customer Upload',
};


export const userTypeOptions: { [key in userType]: string } = {
	SALESPERSON: 'Salesperson',
	USER: 'User',
	ADMIN: 'Admin',
	ORGANISATION_MANAGER: 'Organisation Manager',
	SUPER_USER: 'Super User',
};

export const auditCategoryOptions: { [key in auditCategory]: string } = {
	CREDIT_DEFAULT: 'Credit Default',
	ADVERSE_ALERT: 'Adverse Alert',
	RISK_SCORE: 'Risk Score',
	ENTITY_UPDATE: 'Entity Update',
	OTHER: 'Other',
};

export const customerAuditCategoryOptions: { [key in customerAuditCategory]: string } = {
	ACCOUNT_INFORMATION: 'Account Information',
	ATO_DEFAULT: 'ATO Default',
	ADVERSE_ALERT: 'Adverse Alert',
	BUSINESS_INFORMATION: 'Business Information',
	COMMENT: 'Comment',
	RISK_SCORE: 'Risk Score',
	REFERRED_FOR_COLLECTION: 'Referred for Collection',
	OTHER: 'Other',
	ENTITY_UPDATE: 'Entity Update'
};

export function getAuditCategoryLabel(
	category: CombinedCustomerAuditCategory
): string {
	if (category in customerAuditCategoryOptions) {
		return customerAuditCategoryOptions[category as customerAuditCategory];
	}

	if (category in auditCategoryOptions) {
		return auditCategoryOptions[category as auditCategory];
	}

	// Optional: fallback if key not found
	return category;
}

export const customerApplicationNoteTypeComboboxOptions: ComboboxOption<customerApplicationNoteType>[] = [
	{
		display: 'Comment',
		value: 'COMMENT',
	},
	{
		display: 'Decision',
		value: 'DECISION',
		disabled: true
	},
];

export const SERIAL_NUMBER_TYPE_OPTIONS: ComboboxOption<SerialNumberType>[] = [
	{ display: 'Vehicle Identification Number (VIN)', value: 'VIN' },
	{ display: 'Chassis Number', value: 'CHASSIS' },
	{ display: 'Manufacturers Serial Number', value: 'MANUFACTURERS_SERIAL' },
];

export const WATERCRAFT_SERIAL_NUMBER_TYPE_OPTIONS: ComboboxOption<WatercraftSerialNumberType>[] = [
	{ display: 'Official Number', value: 'OFFICIAL_NUMBER' },
	{ display: 'HIN', value: 'HIN' },
];

export const automatedPpsrGoniSourceOptions: { [key in automatedPpsrGoniSource]: string } = {
	ABN: 'ABN',
	ACCOUNT_NUMBER: 'Account Number',
	APPLICATION_ID: 'Application ID',
	ABN_ENTITY_NAME: 'Entity Name',
};

export const GONI_SOURCE_OPTIONS: { display: string, value: automatedPpsrGoniSource }[] =
	(Object.entries(automatedPpsrGoniSourceOptions) as [automatedPpsrGoniSource, string][])
		.map(([value, display]) => ({ value, display }));
